import { memo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseRow, BaseSelect, BaseTimePicker, SelectPriority } from '~/components/common';
import { getIn, useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { CloseIcon, TrashIcon } from '~/assets/icons';
import { cast, catchError, handleApiError, notify } from '~/utils/helper';
import { IEditEventPayload } from '~/utils/interface/schedule';
import { EVENT_COLOR_DEFAULT } from '~/utils/constants/schedules';
import SelectAssignee from '~/components/common/select-assignee/SelectAssignee';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetSelectAccounts } from '~/thunks/account/accountThunk';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_EVENT_COLORS } from '~/utils/constants/master';
import { handleAddEvent, handleUpdateEvent } from '~/thunks/schedule/scheduleThunk';
import jobApi from '~/apis/job';
import { IJobSelection } from '~/utils/interface/job';
import { SELECTION_TYPES } from '~/utils/constants/jobs';
import scheduleEventApi from '~/apis/schedule';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import taskApi from '~/apis/task';
import { IJobTaskSelection, ITaskSelectionList } from '~/utils/interface/task';
import LinkAddress from '~/components/link-address/LinkAddress';
import { handleGetJob } from '~/thunks/job/jobThunk';
import { handleGetTaskSelection } from '~/thunks/task/taskThunk';

type ConditionalData<T extends 'create' | 'edit'> = T extends 'edit' ? IEditEventPayload : undefined;

type EditEventDialogProps<T extends 'create' | 'edit'> = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  mode: T;
  data: ConditionalData<T>;
  jobId?: string;
};
const initialValues: IEditEventPayload = {
  name: '',
  jobId: '',
  taskId: '',
  description: '',
  startAt: undefined,
  endAt: undefined,
  startTime: '',
  endTime: '',
  priority: '',
  isUpdateAll: false,
  color: EVENT_COLOR_DEFAULT,

  eventGroup: {
    type: 'never',
  },
};

const REPEAT_TYPE = [
  {
    id: 'never',
    name: 'Never',
  },
  {
    id: 'daily',
    name: 'Daily',
  },
  {
    id: 'weekly',
    name: 'Weekly',
  },
  {
    id: 'monthly',
    name: 'Monthly',
  },
];

const WEEK_DAYS = [
  {
    id: 0,
    name: 'Sun',
  },
  {
    id: 1,
    name: 'Mon',
  },
  {
    id: 2,
    name: 'Tue',
  },
  {
    id: 3,
    name: 'Wed',
  },
  {
    id: 4,
    name: 'Thu',
  },
  {
    id: 5,
    name: 'Fri',
  },
  {
    id: 6,
    name: 'Sat',
  },
];

const EditEventDialog = <T extends 'create' | 'edit'>(props: EditEventDialogProps<T>) => {
  //#region Destructuring Props
  const { data, mode = DIALOG_MODE_CREATE } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { taskSelectionList } = useAppSelector((state) => state.task);
  const tasksWithoutJob = taskSelectionList.filter((task) => !task.job);

  const permissions = usePermission();
  const [projectsSelection, setProjectsSelection] = useState<Array<IJobSelection>>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [originalData, setOriginalData] = useState<IEditEventPayload | null>(null);
  const [isUpdateAll, setIsUpdateAll] = useState(false);
  const [isDeleteAll, setIsDeleteAll] = useState(false);
  const [jobTaskSelection, setJobTaskSelection] = useState<ITaskSelectionList | Array<IJobTaskSelection>>(
    taskSelectionList
  );
  const [address, setAddress] = useState<string>('');
  const { t } = useTranslation(['admin/schedule', 'admin/common']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false, // Prevent form reset API error
    initialValues: props.jobId ? { ...initialValues, jobId: props.jobId } : initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleAddEvent(values)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }

      if (mode === DIALOG_MODE_EDIT) {
        try {
          if (data?.id) {
            await dispatch(handleUpdateEvent({ id: data.id, data: values })).unwrap();
            notify('success', t('editSuccess', { ns: 'admin/common' }));
            handleSetOpen(false, true);
          }
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });
  useEffect(() => {
    if (form.values.jobId) {
      (async () => {
        try {
          const job = await dispatch(handleGetJob(form.values.jobId.toString())).unwrap();
          setAddress(job?.fullAddress ?? '');
        } catch (e) {
          handleApiError(e);
        }
      })();
    } else {
      setAddress('');
    }
  }, [form.values.jobId, dispatch]);
  //#endregion Declare Hook

  //#region Selector
  const { selectAccounts } = useAppSelector((state) => state.account);
  const { eventColors } = useAppSelector((state) => state.master);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    getJobSelections();
    dispatch(handleGetTaskSelection());
  }, [dispatch]);

  useEffect(() => {
    if (mode === DIALOG_MODE_CREATE) {
      form.setFieldValue('priority', 'normal');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    dispatch(handleGetSelectAccounts({}));
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_EVENT_COLORS],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setOriginalData(data);
      form.setValues({
        ...data,
        id: undefined,
        startAt: dayjs(data.startAt).format('YYYY-MM-DD'),
        endAt: dayjs(data.endAt).format('YYYY-MM-DD'),
        startTime: dayjs(data.startTime, 'HH:mm:ssZ').format('HH:mm'),
        endTime: dayjs(data.endTime, 'HH:mm:ssZ').format('HH:mm'),
        jobId: cast(data.jobId ?? ''),
        taskId: cast(data.taskId ?? ''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (form.values.jobId) {
      getJobTaskSelection(form.values.jobId);
    } else {
      setJobTaskSelection(tasksWithoutJob);
    }

    if (props.jobId && data && form.values.jobId === props.jobId) {
      form.setFieldValue('taskId', data.taskId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, props.jobId, form.values.jobId, props.open]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleDayClick = (dayName: string) => {
    // Find the id of the clicked day
    const dayId = WEEK_DAYS.find((day) => day.name === dayName)?.id;
    const repeatOn = form.values.eventGroup?.repeatOn ?? [];
    if (dayId === undefined) return; // If the id is not found, do nothing

    const isSelected = repeatOn.includes(dayId);
    const updatedSelectedDays = isSelected
      ? repeatOn.filter((d) => d !== dayId) // Remove the id if already selected
      : [...repeatOn, dayId]; // Add the id if not selected

    form.setFieldValue('eventGroup.repeatOn', updatedSelectedDays);
  };
  const getJobSelections = async () => {
    try {
      const type = SELECTION_TYPES.SCHEDULE;
      const response = await jobApi.getJobsSelection(type);
      const data = response.data.data.responses || [];
      setProjectsSelection([{ id: '', name: 'N/A' }, ...data]);
    } catch (error: unknown) {
      catchError(error, (message) => {
        notify('error', message);
      });
    }
  };

  const getJobTaskSelection = async (jobId: string) => {
    try {
      const tasks = await taskApi.getJobTaskSelection(jobId);
      setJobTaskSelection(tasks.data.data.responses);
    } catch (error) {}
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    props.setOpen(open, needRefreshData);
  };

  const isEventGroupChanged = () => {
    if (!originalData || !originalData.eventGroup || !form.values.eventGroup) return false;

    return (
      form.values.eventGroup.type !== originalData.eventGroup.type ||
      form.values.eventGroup.repeatFrequency !== originalData.eventGroup.repeatFrequency ||
      JSON.stringify(form.values.eventGroup.repeatOn) !== JSON.stringify(originalData.eventGroup.repeatOn) ||
      form.values.eventGroup.endAt !== originalData.eventGroup.endAt
    );
  };
  const handleSaveClick = () => {
    if (mode === DIALOG_MODE_CREATE) {
      form.handleSubmit();
    } else if (mode === DIALOG_MODE_EDIT) {
      if (isEventGroupChanged()) {
        form.setFieldValue('isUpdateAll', true);
        form.submitForm();
      } else if (originalData?.eventGroup?.type === 'never' && !isEventGroupChanged()) {
        form.setFieldValue('isUpdateAll', false);
        form.submitForm();
      } else {
        setConfirmDialogOpen(true);
      }
    }
  };
  const handleDelete = async () => {
    if (originalData?.eventGroup?.type === 'never') {
      try {
        if (data?.id) {
          await scheduleEventApi.deleteEvent({
            ids: [data.id],
            isDeleteAll: false,
          });
        }
        notify('success', t('deleteSuccessMessage'));
        dispatch(scheduleActions.changeNeedRefreshData(true));
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
      }
      handleConfirmDialogClose();
      handleSetOpen(false, true);
    } else {
      setConfirmDeleteDialogOpen(true);
    }
  };
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmDeleteDialogOpen(false);
  };

  const handleConfirmSave = () => {
    form.setFieldValue('isUpdateAll', isUpdateAll);
    form.submitForm(); // Submit form after confirmation
    handleConfirmDialogClose();
  };

  const handleConfirmDelete = async () => {
    if (isDeleteAll) {
      try {
        if (data?.id) {
          await scheduleEventApi.deleteEvent({
            ids: [data?.id],
            isDeleteAll: true,
          });
        }
        notify('success', t('deleteSuccessMessage'));
        dispatch(scheduleActions.changeNeedRefreshData(true));
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
      }
    } else {
      try {
        if (data?.id) {
          await scheduleEventApi.deleteEvent({
            ids: [data.id],
            isDeleteAll: false,
          });
        }
        notify('success', t('deleteSuccessMessage'));
        dispatch(scheduleActions.changeNeedRefreshData(true));
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
      }
    }
    handleConfirmDialogClose();
    handleSetOpen(false, true);
  };
  //#endregion Handle Function

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle>
          {mode === DIALOG_MODE_CREATE ? t('editEventDialog.addTitle') : t('editEventDialog.editTitle')}
          <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
            <CloseIcon className='w-[10px] h-[10px]' />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel required>{t('editEventDialog.name')}</InputLabel>
                <TextField
                  name='name'
                  placeholder={t('editEventDialog.name')}
                  value={form.values.name}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.name)}
                  helperText={form.errors?.name}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('editEventDialog.projectName')}</InputLabel>
                <BaseSelect
                  name='jobId'
                  isSearchEnable={true}
                  placeholder={t('editEventDialog.selectProject')}
                  value={form.values.jobId}
                  options={projectsSelection}
                  onChange={(e) => {
                    form.setFieldValue('taskId', '');
                    form.setFieldValue('jobId', e.target.value);
                  }}
                  error={Boolean(form.errors.jobId)}
                  helperText={form.errors.jobId}
                />
              </Box>
            </BaseRow>

            <Box className='mb-[20px]'>
              <InputLabel>{t('editEventDialog.task')}</InputLabel>
              <BaseSelect
                name='taskId'
                placeholder={t('editEventDialog.selectTask')}
                value={cast(form.values.taskId)}
                options={jobTaskSelection as IJobTaskSelection[]}
                onChange={form.handleChange}
                error={Boolean(form.errors.taskId)}
                helperText={form.errors.taskId}
                label={
                  tasksWithoutJob && tasksWithoutJob?.length === 0 && !form?.values?.jobId
                    ? 'Please select a Project'
                    : ''
                }
              />
            </Box>
            <Box className='mb-[20px]'>
              <InputLabel>{t('editEventDialog.address')}</InputLabel>
              <Typography variant='body2'>
                <LinkAddress address={address} />
              </Typography>
            </Box>
            <Box className='mb-[20px]'>
              <InputLabel>{t('editEventDialog.description')}</InputLabel>
              <TextField
                name='description'
                placeholder={t('editEventDialog.addDescription')}
                value={form.values.description}
                onChange={form.handleChange}
                error={Boolean(form.errors.description)}
                helperText={form.errors.description}
                multiline
              />
            </Box>

            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel required>{t('editEventDialog.dueDate')}</InputLabel>
                <BaseDatePicker
                  placeholder={t('editEventDialog.dueDate')}
                  value={form.values.startAt}
                  onChange={(date) => {
                    if (date) {
                      const selectedDate = dayjs(date).format('MMM DD, YYYY');
                      form.setFieldValue('startAt', selectedDate);
                      form.setFieldValue('endAt', selectedDate);
                    }
                  }}
                  error={Boolean(form.errors.startAt) || Boolean(form.errors.endAt)}
                  helperText={form.errors.startAt || form.errors.endAt}
                />
              </Box>
              <BaseRow className='w-full gap-[20px]'>
                <Box className='w-full'>
                  <InputLabel required>{t('editEventDialog.startTime')}</InputLabel>
                  <BaseTimePicker
                    placeholder={t('editEventDialog.startTime')}
                    value={form.values.startTime ? dayjs(form.values.startTime, 'HH:mm').toDate() : undefined}
                    onChange={(time) => {
                      const startTime = dayjs(time).format('HH:mm');
                      form.setFieldValue('startTime', startTime);
                    }}
                    error={Boolean(form.errors.startTime)}
                    helperText={form.errors.startTime}
                  />
                </Box>
                <Box className='w-full'>
                  <InputLabel required>{t('editEventDialog.endTime')}</InputLabel>
                  <BaseTimePicker
                    placeholder={t('editEventDialog.endTime')}
                    value={form.values.endTime ? dayjs(form.values.endTime, 'HH:mm').toDate() : undefined}
                    onChange={(time) => {
                      const endTime = dayjs(time).format('HH:mm');
                      form.setFieldValue('endTime', endTime);
                    }}
                    error={Boolean(form.errors.endTime)}
                    helperText={form.errors.endTime}
                  />
                </Box>
              </BaseRow>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <BaseRow className='w-full gap-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editEventDialog.repeat')}</InputLabel>
                  <BaseSelect
                    name='eventGroup.type'
                    value={form.values?.eventGroup?.type}
                    options={REPEAT_TYPE}
                    onChange={(e) => {
                      const newType = e.target.value;
                      form.setFieldValue('eventGroup.type', newType);

                      form.setFieldValue('eventGroup.repeatOn', undefined);
                      if (newType === 'never') {
                        form.setFieldValue('eventGroup.endAt', undefined);
                        form.setFieldValue('eventGroup.repeatFrequency', undefined);
                      }
                    }}
                    error={Boolean(getIn(form.errors, 'eventGroup.type'))}
                    helperText={getIn(form.errors, 'eventGroup.type')}
                  ></BaseSelect>
                </Box>
                {form.values.eventGroup?.type !== 'never' ? (
                  <Box className='w-full'>
                    {form.values.eventGroup?.type === 'daily' && (
                      <InputLabel>{t('editEventDialog.repeatEveryDays')} </InputLabel>
                    )}
                    {form.values.eventGroup?.type === 'weekly' && (
                      <InputLabel>{t('editEventDialog.repeatEveryWeeks')} </InputLabel>
                    )}
                    {form.values.eventGroup?.type === 'monthly' && (
                      <InputLabel>{t('editEventDialog.repeatEveryMonths')} </InputLabel>
                    )}
                    <TextField
                      name='eventGroup.repeatFrequency'
                      value={form.values.eventGroup?.repeatFrequency}
                      onChange={form.handleChange}
                      error={Boolean(getIn(form.errors, 'eventGroup.repeatFrequency'))}
                      helperText={getIn(form.errors, 'eventGroup.repeatFrequency')}
                    />
                  </Box>
                ) : (
                  <Box className='w-full'></Box>
                )}
              </BaseRow>
              {form.values.eventGroup?.type !== 'never' ? (
                <Box className='w-full'>
                  <InputLabel>{t('editEventDialog.end')}</InputLabel>
                  <BaseDatePicker
                    placeholder={t('editEventDialog.end')}
                    value={form.values.eventGroup?.endAt}
                    onChange={(date) => {
                      if (date) {
                        const selectedDate = dayjs(date).format('MMM DD, YYYY');

                        form.setFieldValue('eventGroup.endAt', selectedDate);
                      }
                    }}
                    error={Boolean(getIn(form.errors, 'eventGroup.endAt'))}
                    helperText={getIn(form.errors, 'eventGroup.endAt')}
                  />
                </Box>
              ) : (
                <Box className='w-full'></Box>
              )}
            </BaseRow>
            {(form.values.eventGroup?.type === 'weekly' || form.values.eventGroup?.type === 'monthly') && (
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>{t('editEventDialog.repeatOn')}</InputLabel>

                  {form.values.eventGroup?.type === 'monthly' && (
                    <TextField
                      name='repeatOn'
                      placeholder={t('editEventDialog.repeatOn')}
                      value={
                        Array.isArray(form.values.eventGroup?.repeatOn)
                          ? form.values.eventGroup.repeatOn.join(', ')
                          : ''
                      } // Fallback to an empty string if repeatOn is not an array
                      onChange={(event) => {
                        const value = event.target.value;

                        // Split the value by commas, trim whitespace, and convert to numbers
                        const newValues = value
                          .split(',')
                          .map((v) => v.trim()) // Trim any extra spaces
                          .filter((v) => /^\d+$/.test(v)) // Ensure only numeric values
                          .map((v) => Math.min(31, Math.max(1, Number(v)))); // Ensure numbers are between 1 and 31

                        form.setFieldValue('eventGroup.repeatOn', newValues);
                      }}
                      error={Boolean(getIn(form.errors, 'eventGroup.repeatOn'))}
                      helperText={getIn(form.errors, 'eventGroup.repeatOn')}
                    />
                  )}

                  {form.values.eventGroup?.type === 'weekly' && (
                    <Box className='flex flex-row bg-white w-full h-[36px] border-[1px] border-[#E5EAEF] rounded-[8px] items-center justify-between px-[15px]'>
                      {WEEK_DAYS.map((day) => (
                        <Box
                          key={day.id}
                          onClick={() => handleDayClick(day.name)}
                          className={`w-[34px] h-[34px] p-[2px] rounded-[999px] cursor-pointer flex items-center justify-center ${
                            form.values.eventGroup?.repeatOn?.includes(day.id)
                              ? 'bg-[#007AFC] text-white'
                              : 'border-[#E5EAEF] border-[1px]'
                          }`}
                        >
                          <Box>{day.name}</Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Box className='w-full'> </Box>
              </BaseRow>
            )}

            <BaseRow className='gap-[20px] mb-[20px]'>
              <Box className='w-full'>
                <InputLabel required>{t('editEventDialog.priority')}</InputLabel>
                <SelectPriority
                  name='priority'
                  value={form.values.priority}
                  onChange={form.handleChange}
                  error={Boolean(form.errors?.priority)}
                  helperText={form.errors?.priority}
                />
              </Box>
              <Box className='w-full'>
                <InputLabel>{t('editEventDialog.color')}</InputLabel>
                <Box className='flex flex-row bg-white w-full h-[36px] border-[1px] border-[#E5EAEF] rounded-[8px] items-center justify-between px-[15px]'>
                  {eventColors &&
                    eventColors.map((color, index) => (
                      <Box
                        key={index}
                        onClick={() => form.setFieldValue('color', color)}
                        className={`w-[24px] h-[24px] p-[2px] rounded-[999px] cursor-pointer flex items-center justify-center ${
                          color === form.values.color && 'border-[#007AFC] border-[1px]'
                        }`}
                      >
                        <Box className={`w-full h-full rounded-[999px]`} sx={{ backgroundColor: color }}></Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            </BaseRow>

            <Box className='w-full'>
              <InputLabel>{t('editEventDialog.assignees')}</InputLabel>
              <SelectAssignee
                name='assignees'
                onChange={form.handleChange}
                options={selectAccounts}
                value={form.values.assignees}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <>
            {permissions[PERMISSIONS.schedule_delete] && (
              <Button
                className={`visibility: ${Boolean(props.data?.id) ? 'visible' : 'hidden'}`}
                onClick={() => handleDelete()}
                variant='text'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='red' />}
              >
                {t('delete', { ns: 'admin/common' })}
              </Button>
            )}
            <Button color='secondary' variant='contained' onClick={() => handleSetOpen(false)}>
              {t('cancel', { ns: 'admin/common' })}
            </Button>
            {(!permissions[PERMISSIONS.schedule_create] && !permissions[PERMISSIONS.schedule_edit]) || (
              <Button color='primary' variant='contained' onClick={() => handleSaveClick()}>
                {t('save', { ns: 'admin/common' })}
              </Button>
            )}
          </>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle>{t('editRecurringEvent')}</DialogTitle>
        <DialogContent>
          <RadioGroup
            name='updateOption'
            value={isUpdateAll ? 'allEvents' : 'thisEvent'}
            onChange={(e) => setIsUpdateAll(e.target.value === 'allEvents')}
          >
            <FormControlLabel value='thisEvent' control={<Radio />} label={t('thisEvent')} />
            <FormControlLabel value='allEvents' control={<Radio />} label={t('allEvents')} />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose}>{t('cancel', { ns: 'admin/common' })}</Button>
          <Button color='primary' variant='contained' onClick={handleConfirmSave}>
            {t('ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle>{t('deleteRecurringEvent')}</DialogTitle>
        <DialogContent>
          <RadioGroup
            name='deleteOption'
            value={isDeleteAll ? 'allEvents' : 'thisEvent'}
            onChange={(e) => setIsDeleteAll(e.target.value === 'allEvents')}
          >
            <FormControlLabel value='thisEvent' control={<Radio />} label={t('thisEvent')} />
            <FormControlLabel value='allEvents' control={<Radio />} label={t('allEvents')} />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose}>{t('cancel', { ns: 'admin/common' })}</Button>
          <Button color='primary' variant='contained' onClick={handleConfirmDelete}>
            {t('ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(EditEventDialog) as typeof EditEventDialog;
